import React, { useEffect, useState, useMemo } from "react";
import { MassUpdateProps } from "../action-types";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Autocomplete from "@mui/material/Autocomplete";
import { TextField } from "@mui/material";
import { customerEditableFields } from "../../customer-list.constant";
import { AlertComponent } from "../../../../common/components";
import { useSetLoading } from "../../../../common/hooks/appearance";
import { SelectInputComponent } from "../../../../common/components";
import SearchableSelectOwner from "../../../../common/components/select-owner/select-owner-compoent";
import "../../../leads/lead-actions/actions.scss";
import "../../../leads/lead-view/lead-view-details/lead-view-details-edit/lead-view-edit-view-full-info/lead-view-edit-view-full-info.scss";
import { makeStyles } from "@mui/styles";
import { OwnerOption } from "../../../../common/components/select-owner/select-owner-compoent";
import { massUpdateCustomers, getUserDetails } from "../../../../api/customer";
import { CountryCodeWithPhoneNumber } from "../../../../common/components";
import { PREFERRED_CONTACT_MODE_OPTIONS } from "../../voice-create-customer/create-customer/create-customer-helper";
import dynamicFieldValidationSchema from "../input-validation";
import { isOnlyCountryCode } from "../../../../utils/utils";
import { getCurrentUser } from "../../../../common/routines/user-operations";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
const MassUpdateOfCampaigns: React.FC<MassUpdateProps> = ({
  customerIds,
  isOpen,
  setMenuItems,
  selectedActions,
  setSelectedCustomersMap,
  fetchCustomers,
  handleMassActionMenuClose,
}) => {
  const [selectedField, setSelectedField] = useState<{
    label: string;
    id: number;
    value: string;
  }>({
    label: "",
    id: 0,
    value: "",
  });
  const [inputValue, setInputValue] = useState<string>("");
  const [disableUpdate, setDisableUpdate] = useState(true);
  const [error, setError] = useState("");
  const [isLoading, setLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // true if screen ≤ 600px
  const dropdownLabels = ["Customer Owner", "Fax", "Preferred Contact Mode"];

  const [selectedCustomerOwner, setSelectedCustomerOwner] =
    useState<OwnerOption>({
      value: "",
      label: "",
      email: "",
      avatar: null,
    });
  const [leadOptions, setLeadOptions] = useState<OwnerOption[]>([]);
  useEffect(() => {
    if (leadOptions?.length) setSelectedCustomerOwner(leadOptions[0]);
  }, [leadOptions]);

  useEffect(() => {
    const fetchCreatedByOptions = async () => {
      try {
        const response = await getUserDetails({}, "Customer Profile");
        const data = response.data.data.data.data;
        const formattedOptions: OwnerOption[] = data.map((user: any) => ({
          value: String(user.id),
          label: `${user.first_name} ${user.last_name}`,
          email: user.email,
          avatar: user.profile_image_url || null,
        }));

        setLeadOptions(formattedOptions);

        const currentUserId = getCurrentUser();
        const matchedUser = formattedOptions.find(
          (opt) => opt.value === String(currentUserId?.id)
        );
        if (matchedUser) {
          setSelectedCustomerOwner(matchedUser);
        }
      } catch (error: any) {
        setAlertMessage(
          error?.response?.data?.message ||
            "Something went wrong please try again."
        );
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    };

    fetchCreatedByOptions();
  }, []);

  const validateFieldInput = async () => {
    const fieldKey = selectedField.value;
    const schema = dynamicFieldValidationSchema[fieldKey];
    if (!schema) return true;

    try {
      let valueToValidate;

      if (fieldKey === "customerOwner") {
        valueToValidate = selectedCustomerOwner?.value || "";
      } else {
        valueToValidate = inputValue;
      }
      if (fieldKey === "fax" && isOnlyCountryCode(inputValue)) {
        setError("Fax cannot be empty");
        return false;
      }
      await schema.validate(valueToValidate, { abortEarly: false });
      setError("");
      return true;
    } catch (err: any) {
      setError(err?.message);
      return false;
    }
  };

  const handleCancel = () => {
    setSelectedField({
      label: "",
      id: 0,
      value: "",
    });
    setInputValue("");
    setSelectedCustomerOwner({
      value: "",
      label: "",
      email: "",
      avatar: null,
    });

    setMenuItems((prevItems) =>
      prevItems.map((item) =>
        item.value === selectedActions
          ? { ...item, isOpen: !item.isOpen }
          : item
      )
    );
    handleMassActionMenuClose();
    setDisableUpdate(true);
  };

  const formatPayLoad = () => {
    let fieldToUpdate;

    if (selectedField.value === "customerOwner") {
      fieldToUpdate = { customerOwnerId: selectedCustomerOwner?.value || null };
    }
    else if (selectedField.value === "preferredContactMode") { 
      fieldToUpdate = { preferredContactMode: inputValue || null };
    }
    else {
      fieldToUpdate = { [selectedField.value]: inputValue };
    }

    const data = {
      customerIds,
      fieldToUpdate,
    };

    return data;
  };

  const handleUpdate = async () => {
    const isValid = await validateFieldInput();
    if (!isValid) return;
    setLoading(true);
    setError("");
    let response;
    try {
      response = await massUpdateCustomers(formatPayLoad());
      if (response?.status === 200) {
        setAlertMessage(
          `${customerIds?.length === 1 ? "Customer" : "Customers(s)"} updated successfully.`
        );
        setAlertType("success");
        setSelectedCustomersMap({});
        fetchCustomers();
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while saving the customers."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
      setAlertOpen(true);
      handleCancel();
    }
  };

  const handleChange = (
    _: any,
    newValue: { label: string; id: number; value: string } | null
  ) => {
    setSelectedField(
      newValue ? { ...newValue } : { label: "", id: 0, value: "" }
    );
    setDisableUpdate(!newValue);
    setInputValue("");
    setError("");
    setSelectedCustomerOwner({ value: "", label: "", email: "", avatar: null });
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target?.value;
    if (e.target.name === "description") {
      setInputValue(input?.slice(0, 150));
    } else if (e.target.name === "company") {
      setInputValue(input?.slice(0, 50));
    } else setInputValue(e.target.value);
    setError("");
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  const useStyles = makeStyles(() => ({
    selected: {},
    rootMenuItem: {
      "&$selected": {
        backgroundColor: "transparent !important",
        "&:hover": {
          backgroundColor: "#8879501A !important",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        },
      },
    },
  }));

  const handleOwnerChange = (owner: any) => {
    setSelectedCustomerOwner(owner);
  };

  return (
    <div className="mass-update-wrapper campaign-mass-update-wrapper customer-mass-update-wrapper">
      <Dialog
        open={isOpen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="update-popup-wrapper"
        slotProps={{
          paper: {
            sx: {
              width: isMobile ? "90%" : "800px", // 90% width on mobile
              maxWidth: "95vw",                  // prevent overflow
              minHeight: "280px",
              borderBottomLeftRadius: '30px',
            borderBottomRightRadius: '30px',
              top: 0,  // add spacing from top on mobile
              margin: 0,
              position: "absolute",
            },
          },
        }}
      >
        <DialogTitle id="alert-dialog-title">{"Update"}</DialogTitle>
        <DialogContent>
          <div className="select-update-wrapper campaign-select-update-wrapper">
            <div className="field-select-wrapper">
              <Autocomplete
                disablePortal={true}
                options={[...customerEditableFields]}
                getOptionLabel={(option) => option.label}
                onChange={handleChange}
                value={
                  [...customerEditableFields].find(
                    (opt) => opt.label === selectedField?.label
                  ) || null
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select a field"
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                  />
                )}
                slotProps={{
                  popper: {
                    sx: { zIndex: 1 },
                  },
                }}
                sx={{
                  width: 250,
                  "& .MuiOutlinedInput-root": {
                    paddingRight: 0,
                    paddingLeft: "9px",
                    padding: 0,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#7D7D7D", // ✅ Default border color
                    },
                  },
                  "& .MuiAutocomplete-input": {
                    padding: "7.5px 5px 7.5px 10px !important",
                  },
                }}
              />
            </div>
            <div className="field-value-wrapper">
              {selectedField?.label &&
                !dropdownLabels.includes(selectedField?.label) && (
                  <>
                    <TextField
                      id="outlined-basic"
                      variant="outlined"
                      required
                      onChange={handleInput}
                      value={inputValue}
                      name={selectedField?.value}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "37px",
                        },
                        paddingLeft: "15px",
                      }}
                      placeholder={`Enter ${selectedField?.label}`}
                    />
                    {error ? <div className="error-message">{error}</div> : ""}
                  </>
                )}
              {selectedField?.label &&
                selectedField?.label === "Customer Owner" && (
                  <div className="mass-update-dropdown-wrapper campaign-mass-update-dropdown-wrapper">
                    <div className="campaign-list-view-details-view-box-full-info-data-section-sub campaign-owner-dropdown">
                      <SearchableSelectOwner
                        fieldName="customerOwner"
                        options={leadOptions}
                        placeholder="Select Owner"
                        optional={true}
                        value={selectedCustomerOwner}
                        onChange={handleOwnerChange}
                      />
                    </div>
                    {error ? <div className="error-message">{error}</div> : ""}
                  </div>
                )}
              {selectedField?.label &&
                selectedField?.label === "Preferred Contact Mode" && (
                  <div className="mass-update-dropdown-wrapper campaign-mass-update-dropdown-wrapper">
                    <div className="campaign-list-view-details-view-box-full-info-data-section-sub">
                      <SelectInputComponent
                        fieldName="preferredContactMode"
                        options={PREFERRED_CONTACT_MODE_OPTIONS}
                        onChange={(e: any) => {
                          setInputValue(e);
                          setError("");
                        }}
                        value={inputValue}
                        placeholder="Select Preferred Contact Mode"
                      />
                    </div>
                    {error ? <div className="error-message">{error}</div> : ""}
                  </div>
                )}
              {selectedField?.label && selectedField?.label === "Fax" && (
                <div className="mass-update-dropdown-wrapper campaign-mass-update-dropdown-wrapper">
                  <div className="campaign-list-view-details-view-box-full-info-data-section-sub">
                    <div className="fax-number-wrapper mass-update-dropdown-wrapper customer-mass-update-fax-number-wrapper">
                      <CountryCodeWithPhoneNumber
                        fieldName="fax"
                        placeholder="Enter fax"
                        onChange={(
                          value: string,
                          country: any,
                          one: any,
                          two: any
                        ) => {
                          const countryCode = country?.dialCode;

                          if (!countryCode) {
                            return;
                          }
                          setError("");
                          setInputValue(two);
                        }}
                        value={inputValue || "+1"}
                        defaultCountry="us"
                        optional={true}
                      />
                      {error ? (
                        <div className="error-message">{error}</div>
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
        <DialogActions className="button-wrapper">
          <Button onClick={handleCancel} className="cancel-btn">
            Cancel
          </Button>
          <Button
            disabled={disableUpdate}
            onClick={handleUpdate}
            autoFocus
            className="update-btn"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default MassUpdateOfCampaigns;
